import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@postdaily/ui/components/alert";
import { Skeleton } from "@postdaily/ui/components/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@postdaily/ui/components/tabs";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { AiAgentsCard } from "@/components/dashboard/ai-agents-card";
import {
  type AnalyticsRange,
  analyticsRanges,
} from "@/components/dashboard/analytics-format";
import { BestTimeHeatmapCard } from "@/components/dashboard/best-time-heatmap-card";
import { DashboardGreeting } from "@/components/dashboard/dashboard-greeting";
import { DoNextCard } from "@/components/dashboard/do-next-card";
import { KpiStrip } from "@/components/dashboard/kpi-strip";
import { MomentumCard } from "@/components/dashboard/momentum-card";
import { NetworkPerformanceCard } from "@/components/dashboard/network-performance-card";
import { NextUpCard } from "@/components/dashboard/next-up-card";
import { OnboardingChecklist } from "@/components/dashboard/onboarding-card";
import { PlanUsageCard } from "@/components/dashboard/plan-usage-card";
import { RecentActivityCard } from "@/components/dashboard/recent-activity-card";
import { UpcomingCard } from "@/components/dashboard/upcoming-card";
import { useCurrentWorkspace } from "@/hooks/use-current-workspace";
import { useTRPC } from "@/utils/trpc";

export const Route = createFileRoute("/(app)/dashboard")({
  component: RouteComponent,
});

const RANGE_LABELS = {
  "7d": "7 days",
  "30d": "30 days",
  "90d": "90 days",
} satisfies Record<AnalyticsRange, string>;

function RouteComponent() {
  const trpc = useTRPC();
  const { currentWorkspace } = useCurrentWorkspace();
  const viewer = useQuery(trpc.viewer.me.queryOptions());

  if (viewer.isPending) {
    return <DashboardSkeleton />;
  }

  if (viewer.isError) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Dashboard unavailable</AlertTitle>
        <AlertDescription>{viewer.error.message}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-6">
      {currentWorkspace ? (
        <>
          <DashboardGreeting userName={viewer.data.user.name} />
          <WorkspaceDashboard
            workspaceId={currentWorkspace.id}
            workspaceName={currentWorkspace.name}
          />
        </>
      ) : (
        <DashboardSkeleton />
      )}
    </div>
  );
}

function WorkspaceDashboard({
  workspaceId,
  workspaceName,
}: {
  workspaceId: string;
  workspaceName: string;
}) {
  const trpc = useTRPC();
  const [range, setRange] = useState<AnalyticsRange>("30d");
  const onboarding = useQuery(
    trpc.onboarding.getProgress.queryOptions({ workspaceId }),
  );

  const showOnboarding =
    onboarding.data &&
    !onboarding.data.isComplete &&
    !onboarding.data.dismissed;

  return (
    <div className="mx-auto flex w-full min-w-0 max-w-[1240px] flex-col gap-5">
      {showOnboarding && onboarding.data ? (
        <OnboardingChecklist
          workspaceId={workspaceId}
          workspaceName={workspaceName}
          progress={onboarding.data}
        />
      ) : null}

      {/* Hero: next up + weekly momentum */}
      <div className="grid min-w-0 gap-4 lg:grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)]">
        <NextUpCard workspaceId={workspaceId} />
        <MomentumCard workspaceId={workspaceId} />
      </div>

      {/* KPI strip with a range control */}
      <div className="flex min-w-0 items-center justify-end overflow-x-auto">
        <Tabs value={range} onValueChange={(value) => setRange(value)}>
          <TabsList variant="line">
            {analyticsRanges.map((value) => (
              <TabsTrigger key={value} value={value}>
                {RANGE_LABELS[value]}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>
      <KpiStrip range={range} workspaceId={workspaceId} />

      {/*
       * Two equal columns whose card stacks are height-balanced so neither
       * side leaves a big empty gap — in both the sparse (fresh workspace) and
       * dense cases. Each column pairs one tall/fixed card (heatmap / plan) with
       * variable list cards. Row pairing reads: actions, analytics, schedule.
       */}
      <div className="grid min-w-0 gap-4 lg:grid-cols-2">
        <div className="flex min-w-0 flex-col gap-4">
          <AiAgentsCard />
          <DoNextCard workspaceId={workspaceId} />
          <BestTimeHeatmapCard workspaceId={workspaceId} />
          <UpcomingCard workspaceId={workspaceId} />
        </div>
        <div className="flex min-w-0 flex-col gap-4">
          <NetworkPerformanceCard range={range} workspaceId={workspaceId} />
          <PlanUsageCard workspaceId={workspaceId} />
          <RecentActivityCard workspaceId={workspaceId} />
        </div>
      </div>
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-2">
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-5 w-72" />
      </div>
      <div className="grid gap-4 lg:grid-cols-[1.55fr_1fr]">
        <Skeleton className="h-64 w-full rounded-2xl" />
        <Skeleton className="h-64 w-full rounded-2xl" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
      <div className="grid gap-4 lg:grid-cols-[1.55fr_1fr]">
        <Skeleton className="h-80 w-full rounded-2xl" />
        <Skeleton className="h-80 w-full rounded-2xl" />
      </div>
    </div>
  );
}
